/**
 * HFT Studio API hooks — TanStack React Query wrappers.
 *
 * All data is historical (changes once per day at most) so we use a
 * generous staleTime to minimise refetches.
 */
import { useQuery } from '@tanstack/react-query';
import type {
  CalendarResponse,
  CandlesResponse,
  ChainResponse,
  DayResponse,
  SymbolsResponse,
  TelemetryResponse,
  TicksResponse,
} from './types';

const BASE = '/api/hft-studio';
const STALE = 5 * 60_000; // 5 min — data rarely changes intraday
const API_KEY = process.env.REACT_APP_API_KEY || '';

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'x-api-key': API_KEY },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json();
}

/* ---------- Calendar ---------- */
export function useCalendar(from: string, to: string) {
  return useQuery<CalendarResponse>({
    queryKey: ['hft-studio', 'calendar', from, to],
    queryFn: () => fetchJson(`/calendar?from=${from}&to=${to}`),
    staleTime: STALE,
    enabled: !!from && !!to,
  });
}

/* ---------- Day ---------- */
export function useDay(date: string | null) {
  return useQuery<DayResponse>({
    queryKey: ['hft-studio', 'day', date],
    queryFn: () => fetchJson(`/day/${date}`),
    staleTime: STALE,
    enabled: !!date,
  });
}

/* ---------- Candles ---------- */
export function useCandles(
  segment: string | null,
  date: string | null,
  symbol: string | null,
  tf: string = '1m',
) {
  return useQuery<CandlesResponse>({
    queryKey: ['hft-studio', 'candles', segment, date, symbol, tf],
    queryFn: () =>
      fetchJson(`/candles?segment=${segment}&date=${date}&symbol=${symbol}&tf=${tf}`),
    staleTime: STALE,
    enabled: !!segment && !!date && !!symbol,
  });
}

/* ---------- Ticks ---------- */
export function useTicks(
  segment: string | null,
  date: string | null,
  symbol: string | null,
  offset: number = 0,
  limit: number = 5000,
) {
  return useQuery<TicksResponse>({
    queryKey: ['hft-studio', 'ticks', segment, date, symbol, offset, limit],
    queryFn: () =>
      fetchJson(
        `/ticks?segment=${segment}&date=${date}&symbol=${symbol}&offset=${offset}&limit=${limit}`,
      ),
    staleTime: STALE,
    enabled: !!segment && !!date && !!symbol,
  });
}

/* ---------- Options chain ---------- */
export function useChain(underlying: string | null, date: string | null) {
  return useQuery<ChainResponse>({
    queryKey: ['hft-studio', 'chain', underlying, date],
    queryFn: () => fetchJson(`/chain?underlying=${underlying}&date=${date}`),
    staleTime: STALE,
    enabled: !!underlying && !!date,
  });
}

/* ---------- Symbols ---------- */
export function useSymbols(segment: string | null, date: string | null, search: string = '') {
  return useQuery<SymbolsResponse>({
    queryKey: ['hft-studio', 'symbols', segment, date, search],
    queryFn: () =>
      fetchJson(`/symbols?segment=${segment}&date=${date}&search=${encodeURIComponent(search)}`),
    staleTime: STALE,
    enabled: !!segment && !!date,
  });
}

/* ---------- Telemetry ---------- */
export function useTelemetry() {
  return useQuery<TelemetryResponse>({
    queryKey: ['hft-studio', 'telemetry'],
    queryFn: () => fetchJson('/telemetry'),
    staleTime: 30_000, // refresh more often
    refetchInterval: 60_000,
  });
}
